import { TimelineConnector, TimelineContent, TimelineDot, TimelineItem, TimelineSeparator } from '@mui/lab';
import { Box, ListItemButton, Stack, Typography } from '@mui/material';
import { setTransportConditionSelectedItem } from 'app/actions/map';
import { Autosizer, Scrollbar } from 'app/components';
import React, { useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { VariableSizeList } from 'react-window';

const MapTransportEventList = (props) => {
  const dispatch = useDispatch();
  const listRef = useRef();
  const conditionItems = useSelector((state) => state.map.transportSelectedItem.conditions);
  const checkedItems = useSelector((state) => state.map.transportConditionFilterCheckedItems);
  const conditionSelectedItem = useSelector((state) => state.map.transportConditionSelectedItem);
  const { ref: mapRef, isHistoryOpen, historySelectedTime } = useSelector((state) => state.map);
  const { renderEmpty } = props;

  const items = useMemo(() => {
    return conditionItems.reduce((accumulator, currentValue) => {
      const currentElement = {
        ...currentValue,
        messages: currentValue.messages.filter((message) => checkedItems.indexOf(message.key) !== -1),
      };

      if (currentElement.messages.length !== 0) {
        return [...accumulator, currentElement];
      }

      return accumulator;
    }, []);
  }, [conditionItems, checkedItems]);

  const itemHeights = useMemo(() => {
    return items.map((item) => {
      return (
        Math.ceil(
          item.messages.reduce((accumulator, currentValue) => {
            switch (currentValue.name) {
              case 'geofence_id':
              case 'ibutton1':
                return accumulator + currentValue.body.length + currentValue.value.length;
              case 'command': {
                let name = '';

                switch (item.data.sender_type) {
                  case 1:
                    name = 'Система';
                    break;
                  case 2:
                    name = 'Пользователь';
                    break;
                  case 3:
                    name = 'Токен';
                }

                return (
                  accumulator +
                  name.length +
                  (item.data.sender ? `«${item.data.sender}»` : '').length +
                  'отправил(-а) команду'.length +
                  `«${currentValue.body}»`.length
                );
              }
              default:
                return accumulator + currentValue.body.length;
            }
          }, 0) / config.mapEventListOptions.itemLetters
        ) *
          16 +
        config.mapEventListOptions.itemHeight
      );
    });
  }, [items]);

  const getItemSize = (index) => {
    return itemHeights[index];
  };

  const itemSelectedIndex = useMemo(() => {
    if (!isHistoryOpen) {
      return items.findIndex((item) => item.datetime === conditionSelectedItem.datetime);
    }

    const id = items.filter((item) => moment(item.datetime).isBefore(historySelectedTime));
    return items.findIndex((item) => item.id === id);
  }, [conditionSelectedItem, isHistoryOpen]);
  const itemHeightsSum = useMemo(() => {
    return itemHeights.slice(0, itemSelectedIndex).reduce((a, b) => a + b, 0);
  }, [itemSelectedIndex]);

  const handleItemClick = (value) => {
    dispatch(setTransportConditionSelectedItem(value));

    mapRef.current.handleClosePopup();
    mapRef.current.handleFlyTo([value.data.lat, value.data.lon]);
  };

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollToItem(itemSelectedIndex !== -1 ? itemSelectedIndex : items.length - 1, 'end');
    }
  }, [checkedItems]);

  useEffect(() => {
    if (listRef.current && itemSelectedIndex === -1) {
      listRef.current.scrollToItem(items.length, 'end');
    }
  }, [items.length]);

  useEffect(() => {
    if (listRef.current) {
      listRef.current.resetAfterIndex(0, true);
    }
  }, [items]);

  return (
    <Box
      sx={(theme) => ({
        padding: theme.spacing(0),
        height: `100%`,
        '.simplebar-offset > div': {
          '&::-webkit-scrollbar': {
            width: 0,
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
            boxShadow: 'none',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'transparent',
            boxShadow: 'none',
          },
        },
        '.simplebar-container': {
          maxHeight: 'none',
        },
      })}>
      <Autosizer>
        {({ height, width }) => (
          <Box sx={{ height, width }}>
            <Scrollbar>
              {({ scrollableNodeRef, contentNodeRef }) => (
                <VariableSizeList
                  ref={listRef}
                  height={height}
                  width={width}
                  itemSize={getItemSize}
                  overscanCount={10}
                  initialScrollOffset={itemHeightsSum > height ? itemHeightsSum : 0}
                  itemCount={items.length}
                  innerRef={contentNodeRef}
                  outerRef={scrollableNodeRef}
                  itemData={{
                    items,
                    itemSelectedIndex,
                    handleItemClick,
                  }}>
                  {MapTransportEventListItem}
                </VariableSizeList>
              )}
            </Scrollbar>
          </Box>
        )}
      </Autosizer>
      {items.length === 0 && renderEmpty()}
    </Box>
  );
};

const MapTransportEventListItem = ({ style, index, data }) => {
  const { items, itemSelectedIndex, handleItemClick } = data;
  const isSuccess = items[index].messages.some((message) => ['guard_out8:on', 'guard_out8:off'].includes(message.key));
  const isAlert = items[index].messages.some((message) => ['guard_status8:on', 'guard_status8:off'].includes(message.key));
  const isWarning = items[index].messages.some((message) => ['event_id:1'].includes(message.key));

  return (
    <TimelineItem
      key={index}
      style={{
        ...style,
        width: 'calc(100% - 24px)',
        top: `${parseFloat(style.top) + (config.mapEventListOptions.itemPadding - 4)}px`,
        left: `${parseFloat(style.left) + (config.mapEventListOptions.itemPadding - 4)}px`,
      }}
      sx={(theme) => ({
        display: 'flex',
        minHeight: 'auto',
        WebkitBoxPack: 'start',
        position: 'relative',
        '&:before': {
          display: 'none',
        },
        '&:first-of-type': {
          '.MuiTimelineConnector-root': {
            marginTop: '10px',
          },
        },
        '&:last-of-type': {
          '.MuiTimelineConnector-root': {
            height: '13px',
            marginBottom: theme.spacing(0),
          },
        },
        '.MuiTimelineItemButton-root': {
          padding: theme.spacing(0),
          cursor: 'default',
          ...(items[index].type === 'message' && {
            cursor: 'pointer',
          }),
        },
        '.MuiTimelineDot-root': {
          position: 'absolute',
          top: '8.5px',
          left: '4px',
          bgcolor: 'background.paper',
          border: '5px solid',
          borderColor: theme.palette.primary.main,
          margin: theme.spacing(0),
          boxShadow: 'none',
          '&.MuiTimelineDot-filledPrimary': {
            bgcolor: theme.palette.primary.main,
          },
        },
        '.MuiTimelineConnector-root': {
          height: '100%',
          border: '1px dashed',
          bgcolor: 'transparent',
          marginBottom: '2px',
          marginRight: '4px',
          flexGrow: 'unset',
          borderColor: theme.palette.primary.main,
        },
        '.MuiTimelineSeparator-root': {
          width: '30px',
          height: '100%',
          flex: 'none',
          position: 'relative',
        },
        '.MuiTimelineContent-root': {
          position: 'relative',
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: theme.palette.divider,
          borderRadius: theme.shape.borderRadius,
          padding: theme.spacing(2),
          marginBottom: '8px',
          '&:before': {
            top: '16px',
            width: '10px',
            left: '-5px',
            height: '10px',
            zIndex: '0',
            content: '""',
            display: 'block',
            position: 'absolute',
            transform: 'translateY(-50%) rotate(45deg)',
            bgcolor: 'background.paper',
            borderLeft: '1px solid',
            borderBottom: '1px solid',
            borderColor: theme.palette.divider,
            ...(isSuccess && {
              bgcolor: '#aaffaa',
            }),
            ...(isAlert && {
              bgcolor: '#ffaaaa',
            }),
            ...(isWarning && {
              bgcolor: '#ffc107',
            }),
          },
          '.MuiTypography-root': {
            fontSize: '12px',
            lineHeight: '16px',
          },
          '.MuiTimelineItemDatetime-root': {
            'h6:last-of-type': {
              marginLeft: '6px',
              color: theme.palette.text.secondary,
            },
          },
          'span.MuiTypography-root': {
            color: theme.palette.text.secondary,
          },
          ...(isSuccess && {
            bgcolor: '#aaffaa',
          }),
          ...(isAlert && {
            bgcolor: '#ffaaaa',
          }),
          ...(isWarning && {
            bgcolor: '#ffc107',
          }),
        },
        ...(itemSelectedIndex !== -1 &&
          itemSelectedIndex === index && {
            '& .MuiTimelineDot-root': {
              bgcolor: theme.palette.primary.main,
            },
          }),
      })}>
      <ListItemButton
        disableTouchRipple
        className='MuiTimelineItemButton-root'
        onClick={() => items[index].type === 'message' && handleItemClick(items[index])}>
        <TimelineSeparator>
          <TimelineDot />
          <TimelineConnector />
        </TimelineSeparator>
        <TimelineContent>
          <Stack direction='column'>
            <Stack direction='row' justifyContent='space-between'>
              <Stack direction='row' className='MuiTimelineItemDatetime-root'>
                <Typography variant='h6'>
                  {items[index].type === 'message'
                    ? `${moment(items[index].datetime).format('DD.MM HH:mm:ss')},`
                    : moment(items[index].datetime).format('DD.MM HH:mm:ss')}
                </Typography>
                <Typography variant='h6'>
                  {items[index].type === 'message' ? moment(items[index].receive_date).format('DD.MM HH:mm:ss') : ''}
                </Typography>
              </Stack>
              <Typography variant='span'>
                {items[index].data.total_mileage > 0 &&
                  `${Math.round(items[index].data.total_mileage / 1000).toLocaleString('ru')} ${config.conditions.total_mileage.unit}`}
              </Typography>
            </Stack>
            <Stack direction='row' justifyContent='space-between'>
              <Typography variant='body1'>
                {items[index].messages
                  .map((message) => {
                    switch (message.name) {
                      case 'geofence_id':
                      case 'ibutton1':
                      case 'bt_id_1':
                        return `${message.body} «${message.value}»`;
                      case 'command':
                        switch (items[index].data.sender_type) {
                          case 1:
                            return `Система отправила команду «${message.body}»`;
                          case 2:
                            return `Пользователь «${items[index].data.sender}» отправил(-а) команду «${message.body}»`;
                          case 3:
                            return `Токен «${items[index].data.sender}» отправил(-а) команду «${message.body}»`;
                        }
                        break;
                      default:
                        return message.body;
                    }
                  })
                  .join(', ')}
              </Typography>
              {items[index].type === 'message' && Number.isInteger(items[index].data.active_sim) && (
                <Typography variant='span'>СИМ_{items[index].data.active_sim + 1}</Typography>
              )}
            </Stack>
          </Stack>
        </TimelineContent>
      </ListItemButton>
    </TimelineItem>
  );
};

export default MapTransportEventList;
